/**
 * SitReclassCard — per-receive Stock-in-Transit reclass Manual Journals
 * (DR Stock on Hand / CR Stock in Transit) for international POs. Mirrors
 * CogsJournalsCard structure for muscle memory. Lifted out of the original
 * XeroAccountingCards so it can be the body of the Stock-in-Transit tab.
 *
 * window.confirm is never used — enable is gated by an AlertDialog.
 */
import { useEffect, useState } from 'react';
import { ChevronDown, Loader2, Play, Ship } from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { useCurrencyFormatter } from '@/hooks/useCurrencyFormatter';
import { cn } from '@/lib/utils';
import {
  AccountCodeSelect,
  JournalLink,
  JournalStatusBadge,
  StatTile,
} from './shared';
import { useDropdownAccounts } from '../lib/useDropdownAccounts';
import { formatDateTime } from '../lib/format';

interface SitReclassCardProps {
  /** Enable, account config, and Process now are adminProcedure on the backend. */
  isAdmin: boolean;
}

export function SitReclassCard({ isAdmin }: SitReclassCardProps) {
  const utils = trpc.useUtils();
  const { format } = useCurrencyFormatter();
  const accounts = useDropdownAccounts(true);

  const settingsQuery = trpc.xero.getSitSettings.useQuery();
  const statsQuery = trpc.xero.getSitStats.useQuery();
  const logQuery = trpc.xero.getSitLog.useQuery({ limit: 20 });

  const settings = settingsQuery.data;
  const stats = statsQuery.data;

  const [draftInTransitCode, setDraftInTransitCode] = useState('');
  const [draftOnHandCode, setDraftOnHandCode] = useState('');
  const [configOpen, setConfigOpen] = useState(false);
  const [confirmEnableOpen, setConfirmEnableOpen] = useState(false);

  useEffect(() => {
    if (!settings) return;
    setDraftInTransitCode(settings.sitInTransitAccountCode);
    setDraftOnHandCode(settings.sitOnHandAccountCode);
  }, [settings]);

  const configDirty =
    settings !== undefined &&
    (draftInTransitCode !== settings.sitInTransitAccountCode ||
      draftOnHandCode !== settings.sitOnHandAccountCode);

  const invalidateAll = () =>
    Promise.all([
      utils.xero.getSitSettings.invalidate(),
      utils.xero.getSitStats.invalidate(),
      utils.xero.getSitLog.invalidate(),
    ]);

  const updateSettings = trpc.xero.updateSitSettings.useMutation({
    onSuccess: async () => {
      await invalidateAll();
      toast.success('Stock-in-transit settings saved');
    },
    onError: (error) => toast.error(error.message || 'Failed to save stock-in-transit settings'),
  });

  const processReclass = trpc.xero.processSitReclass.useMutation({
    onSuccess: async (result) => {
      await invalidateAll();
      if (result.skipped) {
        toast.info('Stock-in-transit reclass is disabled, nothing was processed.');
      } else if (result.failed > 0) {
        toast.error(
          `${result.succeeded} posted, ${result.failed} failed${result.skippedLockedPeriod > 0 ? `, ${result.skippedLockedPeriod} skipped (locked period)` : ''}. Check the log below.`
        );
      } else if (result.attempted === 0) {
        toast.success('Nothing pending to reclassify.');
      } else {
        toast.success(
          `${result.succeeded} journal${result.succeeded === 1 ? '' : 's'} posted${result.skippedLockedPeriod > 0 ? `, ${result.skippedLockedPeriod} skipped (locked period)` : ''}.`
        );
      }
    },
    onError: (error) => toast.error(error.message || 'Failed to process stock-in-transit reclass'),
  });

  const enabled = settings?.sitReclassEnabled ?? false;

  const handleToggle = (next: boolean) => {
    if (next) {
      setConfirmEnableOpen(true);
    } else {
      updateSettings.mutate({ sitReclassEnabled: false });
    }
  };

  const accountLabel = (code: string) => {
    const match = accounts.find((account) => account.account_code === code);
    return match ? `${match.account_code} ${match.name}` : code;
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Ship className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            </div>
            <div>
              <CardTitle className="text-base">Stock-in-transit reclass</CardTitle>
              <CardDescription>
                When an international PO is received, posts a Manual Journal moving the value from
                Stock-in-Transit to Stock-on-Hand.
              </CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-xs text-muted-foreground" id="sit-enabled-label">
              {enabled ? 'On' : 'Off'}
            </span>
            <Switch
              checked={enabled}
              onCheckedChange={handleToggle}
              disabled={settingsQuery.isLoading || updateSettings.isPending || !isAdmin}
              aria-labelledby="sit-enabled-label"
              title={isAdmin ? undefined : 'Enabling stock-in-transit reclass requires Admin access'}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Stats strip */}
        <div className="grid grid-cols-2 gap-2 md:grid-cols-5">
          <StatTile
            label="Pending"
            value={stats ? String(stats.pendingCount) : '-'}
            foot={stats?.pendingLimitReached ? 'first batch' : undefined}
            tone={stats && stats.pendingCount > 0 ? 'amber' : 'neutral'}
          />
          <StatTile
            label="Posted"
            value={stats ? String(stats.succeededCount) : '-'}
            tone={stats && stats.succeededCount > 0 ? 'emerald' : 'neutral'}
          />
          <StatTile
            label="Failed"
            value={stats ? String(stats.failedCount) : '-'}
            tone={stats && stats.failedCount > 0 ? 'amber' : 'neutral'}
          />
          <StatTile
            label="Locked period"
            value={stats ? String(stats.skippedLockedPeriodCount) : '-'}
          />
          <StatTile label="Last push" value={stats ? formatDateTime(stats.lastPushAt) : '-'} />
        </div>

        {/* Collapsible account configuration */}
        <Collapsible open={configOpen} onOpenChange={setConfigOpen}>
          <CollapsibleTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-full justify-between px-2 text-xs"
            >
              <span>
                Account configuration
                {settings && (
                  <span className="ml-2 font-normal text-muted-foreground">
                    {settings.sitInTransitAccountCode} (in transit) to{' '}
                    {settings.sitOnHandAccountCode} (on hand)
                  </span>
                )}
              </span>
              <ChevronDown
                className={cn(
                  'h-3.5 w-3.5 transition-transform motion-reduce:transition-none',
                  configOpen && 'rotate-180'
                )}
                aria-hidden="true"
              />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-3 pt-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="sit-in-transit-account">Stock-in-transit account</Label>
                <AccountCodeSelect
                  id="sit-in-transit-account"
                  value={draftInTransitCode}
                  onChange={setDraftInTransitCode}
                  accounts={accounts}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sit-on-hand-account">Stock-on-hand account</Label>
                <AccountCodeSelect
                  id="sit-on-hand-account"
                  value={draftOnHandCode}
                  onChange={setDraftOnHandCode}
                  accounts={accounts}
                />
              </div>
            </div>
            {configDirty && (
              <Button
                type="button"
                size="sm"
                className="h-8 px-3 text-xs gap-1.5 bg-threadheads-purple text-white hover:bg-threadheads-purple/90"
                disabled={updateSettings.isPending || !draftInTransitCode || !draftOnHandCode || !isAdmin}
                onClick={() =>
                  updateSettings.mutate({
                    sitInTransitAccountCode: draftInTransitCode,
                    sitOnHandAccountCode: draftOnHandCode,
                  })
                }
              >
                {updateSettings.isPending && (
                  <Loader2
                    className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                )}
                Save accounts
              </Button>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Process now */}
        <div className="flex items-center justify-between gap-3 border-t pt-3">
          <p className="text-xs text-muted-foreground">
            Failed pushes retry on the next run. Locked-period skips never retry.
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 shrink-0 px-3 text-xs gap-1.5"
            onClick={() => processReclass.mutate()}
            disabled={!enabled || processReclass.isPending || !isAdmin}
          >
            {processReclass.isPending ? (
              <Loader2
                className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none"
                aria-hidden="true"
              />
            ) : (
              <Play className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {processReclass.isPending ? 'Pushing journals' : 'Process now'}
          </Button>
        </div>

        {/* Recent log */}
        <div className="space-y-2">
          <h4 className="text-sm font-medium">Recent reclasses</h4>
          {logQuery.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading reclass log…</p>
          ) : (logQuery.data?.data.length ?? 0) === 0 ? (
            <p className="text-sm text-muted-foreground">No reclass journals yet.</p>
          ) : (
            <div className="overflow-x-auto rounded-md border">
              <Table className="min-w-[800px]">
                <TableHeader>
                  <TableRow>
                    <TableHead className="px-3 py-1.5">Date</TableHead>
                    <TableHead className="px-3 py-1.5">PO</TableHead>
                    <TableHead className="px-3 py-1.5">SKU</TableHead>
                    <TableHead className="px-3 py-1.5 text-right">Qty</TableHead>
                    <TableHead className="px-3 py-1.5 text-right">Amount</TableHead>
                    <TableHead className="px-3 py-1.5">Journal</TableHead>
                    <TableHead className="px-3 py-1.5">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(logQuery.data?.data ?? []).map((row) => (
                    <TableRow key={row.id}>
                      <TableCell className="px-3 py-1.5 text-xs text-muted-foreground whitespace-nowrap">
                        {formatDateTime(row.pushed_at ?? row.created_at)}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-xs">{row.po_number ?? '-'}</TableCell>
                      <TableCell className="px-3 py-1.5 font-mono text-xs">
                        {row.sku ?? '-'}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right text-xs tabular-nums">
                        {row.quantity_change ?? '-'}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-right text-xs tabular-nums">
                        {format(Number(row.amount_aud), 'AUD')}
                      </TableCell>
                      <TableCell className="px-3 py-1.5 text-xs">
                        <span className="flex items-center gap-1.5">
                          <JournalLink
                            journalId={row.xero_journal_id}
                            journalNumber={row.journal_number}
                          />
                          {row.is_reversal && (
                            <Badge
                              variant="outline"
                              className="px-1.5 text-[10px] font-semibold uppercase tracking-wide"
                            >
                              Reversal
                            </Badge>
                          )}
                        </span>
                      </TableCell>
                      <TableCell className="px-3 py-1.5">
                        <JournalStatusBadge status={row.status} />
                        {row.status === 'failed' && row.error_message && (
                          <p
                            className="mt-0.5 max-w-[220px] truncate text-[11px] text-destructive"
                            title={row.error_message}
                          >
                            {row.error_message}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </CardContent>

      {/* Enable confirmation */}
      <AlertDialog open={confirmEnableOpen} onOpenChange={setConfirmEnableOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Turn on automatic stock-in-transit reclass?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>
                  Whenever an international-supplier PO is received (partial or full), the IMS will
                  post a Manual Journal that moves the value from Stock in Transit to Stock on Hand.
                </p>
                <p>Journal direction:</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>DR {accountLabel(draftOnHandCode || '640')} (Stock on Hand)</li>
                  <li>
                    CR {accountLabel(draftInTransitCode || '632')} (Stock in Transit, or each
                    supplier's configured source account)
                  </li>
                </ul>
                <p>
                  A recovery cron retries any failed pushes at 3:15 AM Melbourne. Confirm account
                  codes with your accountant before enabling.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() =>
                updateSettings.mutate({
                  sitReclassEnabled: true,
                  sitInTransitAccountCode: draftInTransitCode || undefined,
                  sitOnHandAccountCode: draftOnHandCode || undefined,
                })
              }
            >
              Turn on reclass auto-push
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
